import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { BRAND, FOOTER_PORTALS } from "@/lib/brand";
import { ShieldCheck, AlertCircle, KeyRound } from "lucide-react";

export default function Login() {
  const [params] = useSearchParams();
  const role = params.get("role") || "athlete";
  const portalLabel = FOOTER_PORTALS.find((p) => p.role === role)?.label || "Federation Login";

  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user.role) nav(`/dashboard/${user.role}`, { replace: true });
  }, [user, nav]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email, password);
      nav(`/dashboard/${u.role}`, { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-[calc(100vh-60px)] grid grid-cols-1 lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="bg-nkf-blue text-white relative overflow-hidden hidden lg:flex flex-col p-10 xl:p-16">
        <div className="absolute inset-0 diagonal-stripes" />
        <Link to="/" className="relative flex items-center gap-3">
          <img src={BRAND.logo} alt="NKF" className="h-14 w-14 bg-white rounded-full p-1" />
          <div>
            <div className="font-display text-lg leading-tight">National Karate Federation</div>
            <div className="hindi text-nkf-gold">राष्ट्रीय कराटे महासंघ</div>
          </div>
        </Link>
        <div className="relative mt-auto">
          <div className="text-[11px] uppercase tracking-[0.3em] text-nkf-gold">Secure Portal Access</div>
          <h2 className="font-display text-4xl xl:text-5xl mt-3 leading-tight">{BRAND.statement}</h2>
          <p className="hindi text-nkf-gold text-xl mt-3">{BRAND.slogan.join(" | ")}</p>
          <div className="mt-8 inline-flex items-center gap-2 text-white/80 text-xs uppercase tracking-widest">
            <ShieldCheck className="h-4 w-4 text-nkf-gold" /> Bank-grade encryption · Federation issued credentials
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="bg-white flex items-center justify-center p-6 md:p-10">
        <form onSubmit={onSubmit} className="w-full max-w-md" data-testid="login-form">
          <Link to="/" className="lg:hidden flex items-center gap-3 mb-6">
            <img src={BRAND.logo} alt="NKF" className="h-12 w-12 object-contain" />
            <div className="font-display text-nkf-blue text-lg">NKF Bharat</div>
          </Link>
          <div className="text-[11px] uppercase tracking-[0.3em] text-nkf-red font-semibold flex items-center gap-2">
            <KeyRound className="h-3.5 w-3.5" /> {portalLabel}
          </div>
          <h1 className="font-display text-3xl md:text-4xl text-nkf-blue mt-2">Sign in to your portal</h1>
          <p className="text-sm text-slate-600 mt-2">Use your federation-issued email and password.</p>

          {err && (
            <div className="mt-5 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 p-3 rounded text-sm" data-testid="login-error">
              <AlertCircle className="h-4 w-4 mt-0.5" /> {err}
            </div>
          )}

          <div className="mt-6 space-y-4">
            <label className="block">
              <span className="block text-[12px] uppercase tracking-widest text-slate-600 mb-1.5 font-semibold">Email</span>
              <input data-testid="login-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-nkf-blue/30 focus:border-nkf-blue" />
            </label>
            <label className="block">
              <span className="block text-[12px] uppercase tracking-widest text-slate-600 mb-1.5 font-semibold">Password</span>
              <input data-testid="login-password" required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-nkf-blue/30 focus:border-nkf-blue" />
            </label>
            <button
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-nkf-blue text-white uppercase tracking-widest text-sm font-bold py-3 rounded hover:bg-nkf-navy disabled:opacity-60 transition"
            >
              {loading ? "Signing in…" : "Sign In Securely"}
            </button>
          </div>

          <div className="mt-6 text-sm text-slate-600">
            Don&apos;t have an account?{" "}
            <Link to="/register" className="font-semibold text-nkf-blue hover:text-nkf-red">Register with the Federation</Link>
          </div>
          <div className="mt-2 text-xs text-slate-500">
            Try other portals:
            <div className="mt-2 flex flex-wrap gap-1.5">
              {FOOTER_PORTALS.slice(0, 6).map((p) => (
                <Link key={p.role} to={`/login?role=${p.role}`} className="px-2 py-1 rounded border border-slate-200 text-[11px] uppercase tracking-widest hover:border-nkf-blue hover:text-nkf-blue">
                  {p.label.replace(" Login", "")}
                </Link>
              ))}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
